import { isPlatformBrowser } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    Injector,
    inject,
    input,
    output,
    PLATFORM_ID,
    untracked,
} from '@angular/core';
import {
    CAROUSEL_MOBILE_BREAKPOINT_QUERY,
    CAROUSEL_MOBILE_WINDOW_SIZE,
    CAROUSEL_WINDOW_SIZE,
} from './cost-to-build-client-ui-carousel.constants';
import { CAROUSEL_INITIAL_STATE } from './cost-to-build-client-ui-carousel.state';
import { CarouselStore } from './cost-to-build-client-ui-carousel.store';
import type { CarouselState, CarouselThumbnail } from './cost-to-build-client-ui-carousel.types';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    providers: [CarouselStore],
    selector: 'ctb-carousel',
    styleUrl: './cost-to-build-client-ui-carousel.scss',
    templateUrl: './cost-to-build-client-ui-carousel.html',
})
export class CtbCarouselComponent {
    readonly #destroyRef = inject(DestroyRef);
    readonly #injector = inject(Injector);
    readonly #platformId = inject(PLATFORM_ID);
    readonly #store = inject(CarouselStore);

    readonly ariaLabel = input<string>(CAROUSEL_INITIAL_STATE.ariaLabel);
    readonly id = input<string>(CAROUSEL_INITIAL_STATE.id);
    readonly label = input<string>(CAROUSEL_INITIAL_STATE.label);
    readonly thumbnails = input<readonly CarouselThumbnail[]>(CAROUSEL_INITIAL_STATE.thumbnails);

    /** Emits the selected module id each time the selection changes. */
    readonly thumbnailSelected = output<string>();
    readonly stateChange = output<CarouselState>();

    readonly activeDotIndex = this.#store.activeDotIndex;
    readonly hasBothPeeks = this.#store.hasBothPeeks;
    readonly hasLeftPeek = this.#store.hasLeftPeek;
    readonly state = this.#store.state;
    readonly visibleThumbnails = this.#store.visibleThumbnails;

    #lastEmittedState: CarouselState | null = null;

    readonly syncStoreFromInputs = effect(() => this.#syncStoreFromInputs(), { injector: this.#injector });

    readonly emitStateChanges = effect(
        () => {
            const nextState = this.state();
            const previousSelectedId = this.#lastEmittedState?.selectedId ?? null;

            this.#lastEmittedState = nextState;
            this.stateChange.emit(nextState);

            if (nextState.selectedId && nextState.selectedId !== previousSelectedId) {
                this.thumbnailSelected.emit(nextState.selectedId);
            }
        },
        { injector: this.#injector }
    );

    readonly watchViewportWindowSize = afterNextRender(() => this.#watchViewportWindowSize(), { injector: this.#injector });

    onThumbnailSelect(id: string) {
        this.#store.selectThumbnail(id);
    }

    onDotClick(index: number) {
        this.#store.jumpToDot(index);
    }

    trackById(_index: number, item: CarouselThumbnail) {
        return item.id;
    }

    #syncStoreFromInputs() {
        const patch = {
            ariaLabel: this.ariaLabel(),
            id: this.id(),
            label: this.label(),
            thumbnails: this.thumbnails(),
        };

        untracked(() => this.#store.setState(patch));
    }

    /**
     * Keeps the store's `windowSize` in sync with the `max-width: 768px` breakpoint used
     * in the stylesheet: below it, only one full item plus peeks fit on screen, so the
     * window/peek logic must operate on a single-item window instead of the desktop one.
     */
    #watchViewportWindowSize() {
        if (!isPlatformBrowser(this.#platformId) || typeof window.matchMedia !== 'function') {
            return;
        }

        const mediaQuery = window.matchMedia(CAROUSEL_MOBILE_BREAKPOINT_QUERY);
        const applyWindowSize = (matches: boolean) => {
            this.#store.setState({ windowSize: matches ? CAROUSEL_MOBILE_WINDOW_SIZE : CAROUSEL_WINDOW_SIZE });
        };
        const listener = (event: MediaQueryListEvent) => applyWindowSize(event.matches);

        applyWindowSize(mediaQuery.matches);
        mediaQuery.addEventListener('change', listener);
        this.#destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', listener));
    }
}
